/**
 * Deep clones an object, with support for class instances with a clone method.
 *
 * Class instances without a `clone()` method are copied generically: the clone shares
 * the source's prototype (via Object.create) and its own enumerable properties are deep
 * cloned. This preserves instanceof/methods but does NOT run the constructor and cannot
 * recover `#private` fields. The common built-ins whose data lives in internal slots
 * (Date, RegExp, Map, Set) are special-cased below; other slot-backed types (e.g. typed
 * arrays) need a `clone()` method to round-trip.
 *
 * Circular references and shared references are handled: every object cloned during a call
 * is recorded, so a graph node reached more than once (including a cycle back to an
 * ancestor) resolves to the single clone already made rather than recursing forever or
 * duplicating the node. The one exception is objects cloned via their own `clone()` method
 * — that method owns its traversal, so cycles routed through it are not tracked here.
 */
export default function deepClone<T>(obj: T): T {
	return cloneInternal(obj, new WeakMap<object, unknown>());
}

function cloneInternal<T>(obj: T, seen: WeakMap<object, unknown>): T {
	if (obj === null || typeof obj !== "object") return obj;

	// Already cloned in this pass: return the existing clone so cycles terminate and
	// shared references stay shared (cloned once, not duplicated).
	if (seen.has(obj)) return seen.get(obj) as T;

	// Handle class instances with a clone method
	if ("clone" in obj && typeof obj.clone === "function") {
		const cloned = obj.clone();
		seen.set(obj, cloned);
		return cloned;
	}

	// Handle built-ins whose data lives in internal slots, not enumerable properties.
	if (obj instanceof Date) {
		const cloned = new Date(obj.getTime());
		seen.set(obj, cloned);
		return cloned as T;
	}

	if (obj instanceof RegExp) {
		const cloned = new RegExp(obj.source, obj.flags);
		cloned.lastIndex = obj.lastIndex;
		seen.set(obj, cloned);
		return cloned as T;
	}

	if (obj instanceof Map) {
		const cloned = new Map();
		// Register before recursing so a self-referential entry resolves to this map.
		seen.set(obj, cloned);
		for (const [key, value] of obj) {
			cloned.set(cloneInternal(key, seen), cloneInternal(value, seen));
		}
		return cloned as T;
	}

	if (obj instanceof Set) {
		const cloned = new Set();
		seen.set(obj, cloned);
		for (const value of obj) {
			cloned.add(cloneInternal(value, seen));
		}
		return cloned as T;
	}

	// Handle arrays. Pre-register the empty array, then fill it, so an element that refers
	// back to the array (directly or transitively) resolves to this same clone.
	if (Array.isArray(obj)) {
		const cloned: unknown[] = [];
		seen.set(obj, cloned);
		for (let i = 0; i < obj.length; i++) {
			cloned[i] = cloneInternal(obj[i], seen);
		}
		return cloned as T;
	}

	// Handle plain objects and arbitrary class instances. Preserving the prototype keeps
	// `instanceof` and methods intact for class instances; for plain objects the prototype
	// is just Object.prototype, so this stays a plain object.
	const cloned = Object.create(Object.getPrototypeOf(obj)) as T;
	seen.set(obj, cloned);
	for (const key in obj) {
		if (Object.prototype.hasOwnProperty.call(obj, key)) {
			cloned[key] = cloneInternal(obj[key], seen);
		}
	}

	return cloned;
}
